import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import * as cognito from 'amazon-cognito-identity-js';
import { UserGroups } from '../entities/auth/constants';

export class AuthenticationError extends Error {
	public context: any;
	constructor({ message, context }: { message: string, context?: any }) {
		super(message);
		this.context = context;
	}
}

export const create = (userPool: cognito.CognitoUserPool, username: string, password: string, internalId: string) => {
	const email = new cognito.CognitoUserAttribute({ Name: 'email', Value: username });
	const customId = new cognito.CognitoUserAttribute({ Name: 'custom:internalOwnerId', Value: internalId });
	const attributes = [email, customId];

	const validationApiKey = new CognitoUserAttribute({
		Name: 'apiKey',
		Value: process.env.SIGNUP_PUBLIC_API_KEY ?? ''
	});

	const validationData = [validationApiKey];

	return new Promise<cognito.CognitoUser>((res, rej) => {
		userPool.signUp(username, password, attributes, validationData, (err, result) => {
			if (err) {
				rej(err);
			} else if (result) {
				res(result.user);
			}
		});
	});
};

export const update = async (userPools: cognito.CognitoUserPool[], attributes: { [key: string]: string }) => {
	const user = await getCurrentUser(userPools);
	const attrs = Object.keys(attributes).map((key) => ({ Name: key, Value: attributes[key] }));
	const updated = await new Promise<string>((res, rej) => {
		user.updateAttributes(attrs, (error, status) => {
			error ? rej(error) : res(status || '');
		});
	});
	return updated;
};

export const login = async (userPool: cognito.CognitoUserPool, username: string, password: string) => {
	const user = new cognito.CognitoUser({
		Pool: userPool,
		Username: username.toString().trim(),
	});
	user.setAuthenticationFlowType('USER_PASSWORD_AUTH');

	const details = new cognito.AuthenticationDetails({
		Username: username.toString().trim(),
		Password: password,
	});

	return new Promise<cognito.CognitoUser>((res, rej) =>
		user.authenticateUser(details, {
			onSuccess() {
				res(user);
			},
			onFailure(err) {
				rej(err);
			},
		})
	);

};

export const passwordlessLogin = (userPool: cognito.CognitoUserPool, username: string, session: string, code: string) => {
	const user = new cognito.CognitoUser({
		Pool: userPool,
		Username: username
	});
	(user as any).Session = session;
	return new Promise<cognito.CognitoUser>((res, rej) => user.sendCustomChallengeAnswer(code, {
		onSuccess() {
			res(user);
		},
		onFailure(err) {
			rej(err);
		}
	}));
};

export const logout = async (userPools: cognito.CognitoUserPool[]) => {
	const user = await getCurrentUser(userPools);
	user.signOut();
};

export const changeCurrentUserPassword = async (userPools: cognito.CognitoUserPool[], from: string, to: string) => {
	const user = await getCurrentUser(userPools);
	return new Promise<boolean>((resolve, reject) => {
		user.changePassword(from, to, (error, status) => {
			const success = (!error && (status === 'SUCCESS'));
			if (success) {
				resolve(success);
			} else {
				reject(error);
			}
		});
	});
};

export const requestPasswordReset = (userPool: cognito.CognitoUserPool, username: string) => {
	const user = new cognito.CognitoUser({
		Pool: userPool,
		Username: username
	});

	return new Promise((res, rej) => {
		user.forgotPassword({
			onSuccess(data) {
				res(data);
			},
			onFailure(err) {
				rej(err);
			}
		});
	});
};

export const confirmPasswordReset = (userPool: cognito.CognitoUserPool, username: string, code: string, password: string) => {
	const user = new cognito.CognitoUser({
		Pool: userPool,
		Username: username
	});

	return new Promise<void>((res, rej) => {
		user.confirmPassword(code, password, {
			onSuccess() {
				res();
			},
			onFailure(err) {
				rej(err);
			}
		});
	});
};

export const getUserAttributes = async (user: cognito.CognitoUser) => {
	return new Promise<{ [key: string]: string }>((res, rej) => {
		user.getUserAttributes((err, attributes) => {
			if (err) {
				rej(err);
			} else {
				const attrs: { [key: string]: string } = (attributes || []).reduce((result, attr) => {
					result[attr.getName()] = attr.getValue();
					return result;
				}, {});
				res(attrs);
			}
		});
	});
};

export const getUserGroups = async (user: cognito.CognitoUser) => {
	const session = user.getSignInUserSession();
	const payload = session ? session.getAccessToken().decodePayload() : {};
	return (payload['cognito:groups'] || []) as UserGroups[];
};

export const getCurrentUser = async (userPool: cognito.CognitoUserPool | cognito.CognitoUserPool[]) => {
	const userPools = Array.isArray(userPool) ? userPool : [userPool];
	let error: Error | null = null;
	for (const pool of userPools) {
		try {
			const user = pool.getCurrentUser();
			if (!user) {
				throw new AuthenticationError({ message: 'No user currently logged in' });
			}
			const session = await getSequentialSession(user);
			if (!session || !session.isValid()) {
				throw new AuthenticationError({
					message: 'Current user is not authenticated',
					context: { user: user.getUsername() }
				});
			}
			user.setSignInUserSession(session);
			return user;
		} catch (e) {
			console.log(e);
			error = e;
		}
	}
	throw error || new AuthenticationError({ message: 'No user currently logged in' });
};

const getSession = (user: cognito.CognitoUser) => {
	return new Promise<cognito.CognitoUserSession>((res, rej) => {
		user.getSession((err: any, session: cognito.CognitoUserSession) => {
			const sess = user.getSignInUserSession();
			if (err || !sess) {
				rej(err);
			} else {
				res(sess);
			}
		});
	});
};

const getSequentialSessionFactory = () => {
	const requests: { [username: string]: Promise<cognito.CognitoUserSession> } = {};
	return async (user: cognito.CognitoUser): Promise<cognito.CognitoUserSession> => {
		const key = user.getUsername();
		if (!requests[key]) {
			requests[key] = getSession(user)
				.then((r) => { delete requests[key]; return r; })
				.catch((error) => { delete requests[key]; throw error; });
		}
		const result = await requests[key];
		return result;
	};
};

export const getSequentialSession = getSequentialSessionFactory();

export interface IAuthenticationApi {
	create(username: string, password: string, ownerId: string): Promise<cognito.CognitoUser>;
	update(attributes: { [key: string]: string }): Promise<string>;
	login(username: string, password: string): Promise<cognito.CognitoUser>;
	passwordlessLogin(username: string, session: string, code: string): Promise<cognito.CognitoUser>;
	logout(username: string): Promise<void>;
	changeCurrentUserPassword(from: string, to: string): Promise<boolean>;
	requestPasswordReset(username: string): Promise<any>;
	confirmPasswordReset(username: string, code: string, password: string): Promise<any>;
	getUserAttributes(user: cognito.CognitoUser): Promise<{ [key: string]: string }>;
	getUserGroups(user: cognito.CognitoUser): Promise<UserGroups[]>;
	getCurrentUser(): Promise<cognito.CognitoUser>;
}

const authenticationApiFactory = (): IAuthenticationApi => {
	const USER_POOL_ID = process.env.COGNITO_USER_POOL_ID || 'COGNITO_USER_POOL_ID_ENV_UNSET_ERROR';
	const CLIENT_ID = process.env.COGNITO_CLIENT_ID || 'COGNITO_CLIENT_ID_ENV_UNSET_ERROR';
	const APP_CLIENT_ID = process.env.COGNITO_APP_CLIENT_ID || 'COGNITO_APP_CLIENT_ID_ENV_UNSET_ERROR';

	const userPool = new cognito.CognitoUserPool({
		UserPoolId: USER_POOL_ID,
		ClientId: CLIENT_ID
	});

	const appUserPool = new cognito.CognitoUserPool({
		UserPoolId: USER_POOL_ID,
		ClientId: APP_CLIENT_ID
	});

	const userPools = [userPool, appUserPool];

	return {
		create: (username, password, ownerId) => create(userPool, username.toLowerCase(), password, ownerId),
		update: (attributes) => update(userPools, attributes),
		login: (username, password) => login(userPool, username.toLowerCase(), password),
		passwordlessLogin: (username, session, code) => passwordlessLogin(userPool, username.toLowerCase(), session, code),
		logout: () => logout(userPools),
		changeCurrentUserPassword: (from, to) => changeCurrentUserPassword(userPools, from, to),
		requestPasswordReset: (username) => requestPasswordReset(userPool, username.toLowerCase()),
		confirmPasswordReset: (username, code, password) => confirmPasswordReset(userPool, username.toLowerCase(), code, password),
		getUserAttributes: (user) => getUserAttributes(user),
		getUserGroups: (user) => getUserGroups(user),
		getCurrentUser: () => getCurrentUser(userPools),
	};
};

export default authenticationApiFactory;
